/**
 * Shared colour constants for Crew Forge
 *
 * Consolidates STATUS_COLORS, HIRE_STATUS_COLORS, SUPPLIER_COLORS,
 * SUB_DEPT_COLORS and related label maps that were previously duplicated
 * across multiple files. (URGENCY_COLORS/URGENCY_LABELS retired 2026-08-18.)
 */

import { TASK_STATUS_COLORS } from './taskStatuses';

// ---------------------------------------------------------------------------
// Order Status – Tailwind badge classes (with dark mode)
// Used in: DepartmentOrders, Approvals, MyOrders, OrderPage
// ---------------------------------------------------------------------------
export const STATUS_COLORS = {
  submitted: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  ordered: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  partially_delivered: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  delivered: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  cancelled: 'bg-gray-200/50 text-gray-700 dark:bg-gray-600/50 dark:text-gray-300',
};

// ---------------------------------------------------------------------------
// Order Status – Hex codes for charts (Recharts, etc.)
// Used in: AdminDashboard, DepartmentDashboard, MyDashboard
// ---------------------------------------------------------------------------
export const STATUS_COLORS_HEX = {
  submitted: '#3B82F6',
  approved: '#10B981',
  rejected: '#EF4444',
  ordered: '#F59E0B',
  partially_delivered: '#F97316',
  delivered: '#14B8A6',
  cancelled: '#6B7280',
};

// ---------------------------------------------------------------------------
// Order Status – Human-readable labels
// Used in: DepartmentDashboard, MyDashboard, OrderPage
// ---------------------------------------------------------------------------
/**
 * 🔻 `partially_delivered` IS **'Partial delivery'** SINCE 2026-09-13, AND THE
 * LOWER-CASE `d` IS THE OWNER'S OWN CASING — do not "fix" it to title case.
 *
 * Product owner, asked whether to widen the Status column to fit the old
 * label: *"maybe change the wording to something shorter like 'Partial
 * delivery'"*. It is a WIDTH decision, and the width it bought is measured:
 * the badge needed **137.30px** of cell as 'Partially Delivered' and needs
 * **118.73px** as this, so the Status column is 120 rather than the 140 the old
 * wording forced (`OrderTableView.columnMinimums.test.js` carries the figures,
 * the date and the harness).
 *
 * 🪤 **`StatusBadge` applies NO `uppercase` class** — checked, not assumed — so
 * this string renders to the user exactly as written here. (The column
 * HEADINGS are uppercased by `ui/SortableHeader`'s `TH_BASE`; the badge is
 * not.) Change a word here and you change every badge, the status filter
 * checkbox list, the "Hidden: …" filter chip, `InlineStatusJump`'s dropdown
 * and its toast, the order-list PDF cell, and the Department Orders queue-move
 * toast — all of which read this map rather than a literal. That is the
 * design; a literal anywhere is the drift.
 *
 * ⚠️ **Any figure derived from the WIDTH of a label here expires with the
 * label.** Re-measure in a browser; never reason a new width from an old one.
 */
export const STATUS_LABELS = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  ordered: 'Ordered',
  partially_delivered: 'Partial delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

// 🔻 URGENCY_COLORS / URGENCY_LABELS RETIRED 2026-08-18 with `UrgencyBadge.js`
// (owner ruling — see CLAUDE.md's `LeadTimeRisk.js` bullet). Both were read
// ONLY by that component; nothing else indexed them. Do not reinstate for a
// legacy `orders.urgency` label — nothing writes that column any more either
// (see `constants/leadTime.js` in CLAUDE.md).

// ---------------------------------------------------------------------------
// Hire Status – Hex codes for charts
// Used in: DepartmentDashboard
// ---------------------------------------------------------------------------
export const HIRE_STATUS_COLORS = {
  on_hire: '#3B82F6',
  overdue: '#EF4444',
  pending: '#F59E0B',
  off_hired: '#6B7280',
};

// ---------------------------------------------------------------------------
// Hire Status – Human-readable labels
// Used in: DepartmentDashboard
// ---------------------------------------------------------------------------
export const HIRE_STATUS_LABELS = {
  on_hire: 'On Hire',
  overdue: 'Overdue',
  pending: 'Upcoming',
  off_hired: 'Off-Hired',
};

// ---------------------------------------------------------------------------
// Hire Status – Tailwind badge classes (with dark mode)
// Used in: HireDashboardTable
// ---------------------------------------------------------------------------
export const HIRE_STATUS_BADGES = {
  on_hire: { label: 'On Hire', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
  overdue: { label: 'Overdue', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300' },
  pending: { label: 'Upcoming', bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300' },
  off_hired: { label: 'Off-Hired', bg: 'bg-gray-100 dark:bg-gray-700/50', text: 'text-gray-600 dark:text-gray-300' },
};

// ---------------------------------------------------------------------------
// Hire Status – Calendar view config objects (bg/text/label)
// Used in: CalendarView
// ---------------------------------------------------------------------------
export const HIRE_CALENDAR_COLORS = {
  pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Upcoming' },
  on_hire: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'On Hire' },
  overdue: { bg: 'bg-red-100', text: 'text-red-700', label: 'Overdue' },
  off_hired: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Off-Hired' },
};

// ---------------------------------------------------------------------------
// Chart palette arrays
// Used in: AdminDashboard, DepartmentDashboard, MyDashboard
// ---------------------------------------------------------------------------
export const SUPPLIER_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316',
];

export const SUB_DEPT_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#14B8A6', '#F97316',
  '#6366F1', '#84CC16',
];

export const USER_COLORS = [
  '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B',
  '#EF4444', '#EC4899', '#14B8A6', '#F97316',
  '#6366F1', '#84CC16',
];

// ---------------------------------------------------------------------------
// Equipment Listing Status – Tailwind badge classes (with dark mode)
// Used in: MarketplaceMyListings, AdminSettings equipment categories
// ---------------------------------------------------------------------------
export const LISTING_STATUS_COLORS = {
  active:  'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  paused:  'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  removed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
};

// ---------------------------------------------------------------------------
// Equipment Listing Status – Human-readable labels
// ---------------------------------------------------------------------------
export const LISTING_STATUS_LABELS = {
  active: 'Active',
  paused: 'Paused',
  removed: 'Removed',
};

// ---------------------------------------------------------------------------
// Grading eligibility – Tailwind pill classes (with dark mode)
// Used in: GradeProgressionBar (chip row), Grading page (requirements list)
// ---------------------------------------------------------------------------
export const GRADING_ELIGIBILITY_COLORS = {
  pass:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  borderline: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  fail:       'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// ---------------------------------------------------------------------------
// Grading PDF palette – Hex codes for pdfmake (NOT Tailwind classes)
// Used in: utils/gradingPdf.js — the official JIGS application form headings
// and table rules.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Gag Status – Tailwind badge classes (with dark mode)
// Shared statuses reuse the task colour classes; 'ready' ("Ready to Shoot")
// is gag-specific and gets emerald.
// Used in: GagBreakdown
// ---------------------------------------------------------------------------
export const GAG_STATUS_COLORS = {
  ...TASK_STATUS_COLORS,
  ready: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
};

// ---------------------------------------------------------------------------
// Gag RA-requirement badge – derived ra_status from /api/gags:
// 'required' (flagged, no live RA), 'draft' (linked RA(s), none signed off),
// 'signed_off' (≥1 live signed-off RA). null ra_status renders NO badge.
// Used in: GagGroupTable (row column), GagDetailPanel (RA section header)
// ---------------------------------------------------------------------------
export const GAG_RA_BADGES = {
  required:   { label: 'RA required', cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  draft:      { label: 'RA draft',    cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  signed_off: { label: 'RA ✓',        cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' },
};

// ---------------------------------------------------------------------------
// Gag £ budget-status glyph — sibling of GAG_RA_BADGES, derived from the gag's
// estimated_cost + read-only `budgeted` flag (see utils/gagBudgetStatus.js):
// 'none' (no estimate → faded grey), 'estimated' (estimate set, not in the
// budget → amber), 'budgeted' (estimate set + linked active budget line →
// green). `cls` is the £ glyph's text colour (with dark-mode variant).
// Used in: GagGroupTable (row title), GagDetailPanel (header)
// 🎨 The glyph is TEXT and colour is its only on-screen state channel, so the
// two coloured states take 1.4.3's 4.5:1 — the gag pages' amber-700/amber-400
// pair (GagTimelineView, GagDetailPanel), and green-700/green-400 beside it.
// Measured 2026-10-01 on white: amber-500 was 2.15:1 and green-600 3.30:1
// (both FAIL); amber-700 5.02, green-700 5.02. Dark, on #1e293b: amber-400
// 8.76, green-700 → index.css forces #86efac, 10.42. 'none' stays faint on
// purpose (out of this pass: the app-wide grey muted text).
// ---------------------------------------------------------------------------
export const GAG_BUDGET_BADGES = {
  none:      { label: 'No estimate',        cls: 'text-gray-300 dark:text-gray-600' },
  estimated: { label: 'Estimated',          cls: 'text-amber-700 dark:text-amber-400' },
  budgeted:  { label: 'In the budget',      cls: 'text-green-700 dark:text-green-400' },
};

export const GRADING_PDF_COLORS = {
  orange:     '#E8731C',
  orangeSoft: '#FBE2CD',
  greyRule:   '#888888',
  labelGrey:  '#555555',
};
